import { useParams, Link } from "react-router-dom";
import { BLOG_POSTS } from "../../../data/blogPosts";
import AnimatedBackground from "../../common/AnimatedBackground";
import SEOHead from "../../common/SEOHead";
import { Helmet } from "react-helmet-async";
import { BiCalendar, BiShareAlt, BiTime } from "react-icons/bi";
import { useEffect, useState } from "react";
import Footer from "../Footer/Footer";
import AdUnit from "../../common/AdUnit";

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("es-AR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    try {
      ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
    } catch (e) {
      console.error("AdSense error:", e);
    }
  }, [slug]);

  const handleShare = async () => {
    const shareUrl = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: post?.title,
          text: post?.summary,
          url: shareUrl,
        });
        return;
      } catch {
        // If the user cancels the share sheet, fall back silently.
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt("Copiá el enlace del post", shareUrl);
    }
  };

  if (!post) {
    return (
      <div className="relative w-full min-h-lvh text-white flex items-center justify-center">
        <AnimatedBackground />
        <div className="text-center flex flex-col gap-4 items-center">
          <h1 className="text-4xl font-bold font-header">404</h1>
          <p className="text-gray-400">Artículo no encontrado.</p>
          <Link
            to="/#blog"
            className="text-green-300 hover:underline transition-colors"
          >
            ← Volver al blog
          </Link>
        </div>
      </div>
    );
  }

  // Posts relacionados (otros posts, sin el actual)
  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div className="relative w-full min-h-lvh text-white">
      <SEOHead
        title={post.title}
        description={post.summary}
        image={post.coverImage}
        url={`/blog/${post.slug}`}
        type="article"
        publishedTime={post.date}
        tags={post.tags}
        author="Franco Martino"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.summary,
          image: post.coverImage
            ? `${window.location.origin}/${post.coverImage.replace(/^\//, "")}`
            : undefined,
          datePublished: post.date,
          author: {
            "@type": "Person",
            name: "Franco Martino",
            url: "https://martinofranco.com",
          },
          publisher: {
            "@type": "Person",
            name: "Franco Martino",
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `${window.location.origin}/blog/${post.slug}`,
          },
          keywords: post.tags.join(", "),
        }}
      />
      <AnimatedBackground />

      {/* Breadcrumbs structured data */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Inicio", item: "https://martinofranco.com/" },
              { "@type": "ListItem", position: 2, name: "Blog", item: "https://martinofranco.com/blog" },
              { "@type": "ListItem", position: 3, name: post.title, item: `https://martinofranco.com/blog/${post.slug}` },
            ],
          })}
        </script>
      </Helmet>

      {/* Article */}
      <article className="relative max-w-4xl mx-auto px-6 sm:px-12 pt-6 pb-10">
        {/* Header */}
        <header className="mb-8 flex flex-col gap-4">
          <h1 className="text-3xl sm:text-4xl font-bold font-header leading-tight text-white">
            {post.title}
          </h1>

          <p className="text-gray-400 text-lg leading-relaxed">
            {post.summary}
          </p>

          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <BiCalendar size={15} />
              {formatDate(post.date)}
            </span>
            <span className="flex items-center gap-1.5">
              <BiTime size={15} />
              {post.readingTime} min de lectura
            </span>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors duration-200"
              title="Compartir este post"
              aria-label="Compartir este post"
            >
              <BiShareAlt size={15} />
              <span>{copied ? "Enlace copiado" : "Compartir"}</span>
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs bg-gray-800 border border-gray-700 text-gray-300 px-2.5 py-1 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="rounded-xl border border-gray-700/70 bg-gray-900/60 p-4 text-sm text-gray-400">
            <p className="text-gray-200 font-medium">Autor: Franco Martino</p>
            <p className="mt-1">
              Contenido técnico original basado en experiencia práctica en
              desarrollo de software y automatización.
            </p>
          </div>
        </header>

        {/* Cover image */}
        {post.coverImage && (
          <div className="mb-10 rounded-2xl overflow-hidden shadow-xl shadow-black/30">
            <img
              src={post.coverImage.startsWith("/") || post.coverImage.startsWith("http") ? post.coverImage : `/${post.coverImage}`}
              alt={`Portada del artículo: ${post.title}`}
              className="w-full aspect-[16/9] object-cover"
            />
          </div>
        )}

        {/* Divider */}
        <div className="h-px bg-gray-700 mb-10" />

        {/* Content */}
        <div
          className="prose-blog text-gray-300 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Divider */}
        <div className="h-px bg-gray-700 mt-10 mb-10" />

        {/* Anuncio — después del contenido, antes de posts relacionados */}
        <AdUnit
          slot="6805590889"
          format="horizontal"
          label="Publicidad"
          className="mb-10"
        />

        {/* Related posts */}
        {related.length > 0 && (
          <section>
            <h2 className="text-lg font-bold text-gray-400 font-header mb-4">
              Otros artículos
            </h2>
            <div className="flex flex-col sm:flex-row gap-4">
              {related.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/blog/${rel.slug}`}
                  className="group flex-1 bg-gray-800 hover:bg-gray-700/80 rounded-xl p-4 flex flex-col gap-2 transition-colors duration-300"
                >
                  <span className="text-xs text-gray-500">
                    {formatDate(rel.date)}
                  </span>
                  <span className="text-white font-semibold text-sm group-hover:text-green-300 transition-colors duration-300 leading-snug">
                    {rel.title}
                  </span>
                  <span className="text-gray-400 text-xs mt-auto">
                    {rel.readingTime} min de lectura →
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Back link */}
        <div className="mt-8 flex justify-center">
          <Link
            to="/"
            className="text-gray-500 hover:text-white text-sm transition-colors duration-200 underline underline-offset-4"
          >
            ← Volver al portfolio
          </Link>
        </div>
      </article >
      <Footer />
    </div >
  );
}
